import React, { useState } from "react";
import { typography, surfaces, borders, textColors } from "../../../design-system/tokens";
import { PresetPreviewCard } from "./ElementStylePresetPreviewCard";

/**
 * Group flat preset list by `family.key`.
 * Falls back to a single-member family keyed by the preset value when no family
 * metadata is present (older backends or stand-alone presets).
 */
export function buildPresetFamilies(presets) {
  const familyMap = new Map();
  const order = [];
  for (const preset of Array.isArray(presets) ? presets : []) {
    const key = preset?.family?.key || preset?.value;
    if (!key) continue;
    if (!familyMap.has(key)) {
      familyMap.set(key, {
        key,
        primary: preset.family?.primary || preset.value,
        members: preset.family?.members || [
          { preset: preset.value, name: preset.name, colors: [] },
        ],
        presets: [],
      });
      order.push(key);
    }
    familyMap.get(key).presets.push(preset);
  }
  return order.map((k) => familyMap.get(k));
}

/**
 * Row of color swatches that lets the user switch between the variants of a
 * single preset family. Hidden when the family has only one variant.
 */
export function PresetFamilyVariantDots({
  members,
  presets,
  activeValue,
  onSelect,
  size = "md",
}) {
  const availableMembers = (members || []).filter((m) =>
    presets.some((p) => p.value === m.preset),
  );
  if (availableMembers.length <= 1) return null;

  const dotSizeClass = size === "sm" ? "w-3 h-3" : "w-4 h-4";

  return (
    <div className="flex items-center gap-1.5">
      {availableMembers.map((member) => {
        const isActive = member.preset === activeValue;
        const swatchColors = member.colors || [];
        const dotBg =
          swatchColors.length >= 2
            ? `linear-gradient(135deg, ${swatchColors[0]}, ${swatchColors[1]})`
            : swatchColors[0] || "#888";
        return (
          <button
            key={member.preset}
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onSelect?.(member.preset);
            }}
            title={member.name}
            aria-label={`Preview ${member.name} variant`}
            className={`${dotSizeClass} rounded-full transition-all duration-150 ${
              isActive
                ? "ring-[1.5px] ring-blue-500 ring-offset-1 scale-110"
                : "opacity-70 hover:opacity-100 hover:scale-125"
            }`}
            style={{ background: dotBg }}
          />
        );
      })}
    </div>
  );
}

/**
 * Render a single preset family as one compact card. The header collapses the
 * family name + active style name into a single row alongside the variant dots
 * and the Apply affordance — so multi-variant families take roughly the same
 * vertical space as single-variant ones. The whole card is the click target.
 *
 * Used by both:
 *   - `ElementStylePresetsTab` (Edit Panel "Style Change" section)
 *   - `InlineStylePresetPicker` (quick-action palette in the canvas)
 */
export function ElementStylePresetFamilyCard({
  family,
  elementType,
  applyingPreset,
  onApply,
  scaleMultiplier,
  verticalPaddingPx,
  variantDotSize = "md",
}) {
  const [activeVariantValue, setActiveVariantValue] = useState(family.primary);

  const activePreset =
    family.presets.find((p) => p.value === activeVariantValue) ||
    family.presets[0];

  const availableMembers = (family.members || []).filter((m) =>
    family.presets.some((p) => p.value === m.preset),
  );
  const hasVariants = availableMembers.length > 1;

  const isApplying = applyingPreset === activePreset?.value;
  const isDisabled = Boolean(applyingPreset);
  const familyLabel = family.key.replace(/_/g, " ");
  const styleName = activePreset?.name || "Preset";

  const handleApplyClick = () => {
    if (isDisabled || !activePreset?.value) return;
    onApply?.(activePreset.value);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleApplyClick();
    }
  };

  return (
    <div
      role="button"
      tabIndex={isDisabled ? -1 : 0}
      aria-disabled={isDisabled}
      onClick={isDisabled ? undefined : handleApplyClick}
      onKeyDown={isDisabled ? undefined : handleKeyDown}
      className={`group rounded-xl border ${borders.default} ${surfaces.primary} p-3 transition-colors ${
        isDisabled
          ? "cursor-wait opacity-70"
          : `${surfaces.hover} cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500`
      }`}
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        {/* Title is split into separate spans so accessible-name and exact-text
            queries can match either the family label or the active style name
            independently (e.g. tests that look for just "Midnight"). */}
        <div className={`${typography.bodySmall} min-w-0 flex items-baseline gap-1.5 truncate`}>
          {hasVariants ? (
            <>
              <span className={`${textColors.tertiary} capitalize truncate`}>
                {familyLabel} family
              </span>
              <span className={`${textColors.tertiary} shrink-0`} aria-hidden="true">
                –
              </span>
              <span className={`${textColors.primary} truncate`}>{styleName}</span>
            </>
          ) : (
            <span className={`${textColors.primary} truncate`}>{styleName}</span>
          )}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {hasVariants ? (
            <PresetFamilyVariantDots
              members={family.members}
              presets={family.presets}
              activeValue={activePreset.value}
              onSelect={setActiveVariantValue}
              size={variantDotSize}
            />
          ) : null}
          {isApplying ? (
            <div className={`${typography.caption} ${textColors.tertiary}`}>
              Applying...
            </div>
          ) : null}
        </div>
      </div>

      <PresetPreviewCard
        preset={activePreset}
        elementType={elementType}
        isApplying={isApplying}
        isDisabled={isDisabled}
        onApply={handleApplyClick}
        scaleMultiplier={scaleMultiplier}
        verticalPaddingPx={verticalPaddingPx}
        hideHeader
        chromeless
      />
    </div>
  );
}

export default ElementStylePresetFamilyCard;
